import Features from "@components/Features"
import FrequentlyAskedQuestions from "@components/FrequentlyAskedQuestions"
import GoalSummary from "@components/GoalSummary"
import LimitedTimeOfferBanner from "@components/LimitedTimeOfferBanner"
import LimitedTimeOfferDialog from "@components/LimitedTimeOfferDialog"
import MoneyBackGuarantee from "@components/MoneyBackGuarantee"
import Plans from "@components/Plans"
import Trust from "@components/Trust"
import WhatsAppHighlight from "@components/WhatsAppHighlight"
import { getFunnelSourceFromLocalStorage } from "@helpers/localStorage"
import useCountdownTimer from "@hooks/useCountdownTimer"
import ErrorPage from "@pages/ErrorPage"
import LoadingPage from "@pages/LoadingPage"
import { Suspense, useEffect, useState } from "react"
import { Await, useLoaderData } from "react-router-dom"
import {
  GetPricesData,
  GetPricesLoaderResult,
} from "src/loaders/getPricesLoader"
import { Plan } from "src/types"

const DIALOG_DELAY_MS = 30000
const COUNTDOWN_MINUTES = 9

export default function PlansPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>("yearly")
  const isMonthly = selectedPlan === "monthly"
  const [isExpired, setIsExpired] = useState(false)
  const isWhatsappFunnel = getFunnelSourceFromLocalStorage() === "whatsapp"

  const { endTime } = useCountdownTimer({
    startMinutes: COUNTDOWN_MINUTES,
  })

  useEffect(() => {
    const checkExpired = () => {
      if (Date.now() >= endTime) {
        setIsExpired(true)
      }
    }

    const timer = setInterval(checkExpired, 1000)
    return () => clearInterval(timer)
  }, [endTime])

  const data = useLoaderData() as GetPricesLoaderResult

  useEffect(() => {
    const dialogTimeout = setTimeout(() => {
      setIsDialogOpen(true)
    }, DIALOG_DELAY_MS)

    return () => clearTimeout(dialogTimeout)
  }, [])

  const handleCloseDialog = () => setIsDialogOpen(false)

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data.getPricesData}>
        {(getPricesData: GetPricesData) => {
          const selectedIntroPrice = isMonthly
            ? getPricesData.monthlyIntroPrice
            : getPricesData.yearlyIntroPrice
          const selectedFullPrice = isMonthly
            ? getPricesData.monthlyPrice
            : getPricesData.yearlyPrice

          const plansProps = {
            currency: getPricesData.currency,
            isSubmitting,
            setIsSubmitting,
            endTime,
            weeklyIntroPrice: getPricesData.weeklyIntroPrice,
            weeklyPrice: getPricesData.weeklyPrice,
            monthlyIntroPrice: getPricesData.monthlyIntroPrice,
            monthlyPrice: getPricesData.monthlyPrice,
            yearlyIntroPrice: getPricesData.yearlyIntroPrice,
            yearlyPrice: getPricesData.yearlyPrice,
            selectedPlan,
            onPlanChange: setSelectedPlan,
          }

          return (
            <div>
              {!isExpired && (
                <LimitedTimeOfferBanner
                  currency={getPricesData.currency}
                  fullPrice={selectedFullPrice}
                  isSubmitting={isSubmitting}
                  priceId={selectedIntroPrice?.id}
                  setIsSubmitting={setIsSubmitting}
                  endTime={endTime}
                  isMonthly={isMonthly}
                />
              )}

              <main className="custom-container pt-8 pb-14">
                <section className="mb-10">
                  <GoalSummary />
                </section>

                {isWhatsappFunnel && (
                  <section className="mb-12">
                    <WhatsAppHighlight />
                  </section>
                )}

                <section id="plans" className="mt-9 mb-16">
                  <Plans {...plansProps} />
                </section>

                <section className="mb-12">
                  <Trust />
                </section>

                <section className="mb-12">
                  <Features isWhatsappFunnel={isWhatsappFunnel} />
                </section>

                <section className="mb-12">
                  <MoneyBackGuarantee />
                </section>

                <section className="mb-12">
                  <FrequentlyAskedQuestions />
                </section>

                <section className="mb-16">
                  <Plans {...plansProps} />
                </section>
              </main>

              <LimitedTimeOfferDialog
                currency={getPricesData.currency}
                isSubmitting={isSubmitting}
                onClose={handleCloseDialog}
                open={isDialogOpen}
                plan={selectedPlan}
                setIsSubmitting={setIsSubmitting}
                stripeIntroPrice={selectedIntroPrice}
                stripePrice={selectedFullPrice}
              />
            </div>
          )
        }}
      </Await>
    </Suspense>
  )
}
